// src/App.tsx — GM Ops console (ops.greenmaestro.com). GE-02P v2 + OPS-FINAL phase 1.
// Sign-in: email magic link. Access: ops_whoami() decides — no operator role, no console.
import { lazy, Suspense, useCallback, useEffect, useState, type FormEvent } from "react";
import { BrowserRouter, Navigate, NavLink, Outlet, Route, Routes } from "react-router-dom";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "./lib/supabase";
import { ops, ROLE_LABEL, type WhoAmI } from "./lib/ops";
import { OperatorProvider, ServicesProvider, ToastProvider } from "./lib/context";
import { Notice } from "./components/ui";
import Services from "./pages/Services";
import Quotes from "./pages/Quotes";
import Seasons from "./pages/Seasons";
import Zips from "./pages/Zips";
import Supply from "./pages/Supply";   // SUPPLY (record 100): local supply, next to Orders
import SettingsPage from "./pages/SettingsPage";
import LogPage from "./pages/LogPage";
import Today from "./pages/Today";
import Dispatch from "./pages/Dispatch";
import Maestros from "./pages/Maestros";
import Territories from "./pages/Territories";
import Property from "./pages/Property";   // GM-TXT-03 D3
import Campaigns from "./pages/Campaigns";
import Revisiones from "./pages/Revisiones";   // MDO-6: Manos de Oro
import Pages from "./pages/Pages";   // PAGES-02: product page wording + publish
import Orders from "./pages/Orders";   // PROC-02: order materials
import Fotos from "./pages/Fotos";   // CON-TOOLS-04: Fotos para Maestros
import Sugerencias from "./pages/Sugerencias";   // TELLGM-01: what Maestros send with "Dile al equipo GM"
import Vecinos from "./pages/Vecinos";   // FLY-01: neighbor flyers (puntos)
import Weather from "./pages/Weather";   // WX-01: weather layer (read-only)
// The wizard (and the QR encoder it uses) loads only when someone opens /campaigns/new.
const CampaignWizard = lazy(() => import("./pages/CampaignWizard"));
import "./styles.css";

export default function App() {
  const [session, setSession] = useState<Session | null | undefined>(undefined);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session));
    const { data } = supabase.auth.onAuthStateChange((_event, s) => setSession(s));
    return () => data.subscription.unsubscribe();
  }, []);

  if (session === undefined) return <Splash text="Checking your sign-in…" />;
  return (
    <BrowserRouter>
      <ToastProvider>{session ? <OperatorGate session={session} /> : <Login />}</ToastProvider>
    </BrowserRouter>
  );
}

function Splash({ text }: { text: string }) {
  return (
    <div className="splash">
      <p className="brand-mark">Green Maestro Ops</p>
      <p className="loading">{text}</p>
    </div>
  );
}

// ── Sign in ────────────────────────────────────────────────
function Login() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: { emailRedirectTo: window.location.origin },
    });
    setBusy(false);
    if (err) setError(err.message);
    else setSent(true);
  }

  return (
    <main className="auth">
      <div className="auth-card">
        <p className="brand-mark">Green Maestro Ops</p>
        <h1>Operations console</h1>
        {sent ? (
          <>
            <p>
              We sent a sign-in link to <strong>{email.trim()}</strong>. Open it on this device to continue.
            </p>
            <button type="button" className="btn quiet" onClick={() => setSent(false)}>
              Use a different email
            </button>
          </>
        ) : (
          <form onSubmit={submit}>
            <label htmlFor="email">Work email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <Notice kind="error">{error}</Notice>}
            <button type="submit" className="btn primary wide" disabled={busy || !email.includes("@")}>
              {busy ? "Sending…" : "Email me a sign-in link"}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

// ── Operator gate (also the startup check) ────────────────
function OperatorGate({ session }: { session: Session }) {
  const [who, setWho] = useState<WhoAmI | null>(null);
  const [error, setError] = useState<string | null>(null);

  const check = useCallback(async () => {
    setError(null);
    try {
      setWho(await ops.whoami());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void check();
  }, [check, session.user.id]);

  const signOut = () => void supabase.auth.signOut();

  if (error) {
    return (
      <main className="auth">
        <div className="auth-card">
          <p className="brand-mark">Green Maestro Ops</p>
          <h1>Can't reach the ops service</h1>
          <Notice kind="error">{error}</Notice>
          <div className="row-actions">
            <button type="button" className="btn primary" onClick={() => void check()}>
              Try again
            </button>
            <button type="button" className="btn quiet" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>
      </main>
    );
  }
  if (!who) return <Splash text="Checking your access…" />;
  if (!who.role) {
    return (
      <main className="auth">
        <div className="auth-card">
          <p className="brand-mark">Green Maestro Ops</p>
          <h1>This account isn't an operator</h1>
          <p>
            You're signed in as <strong>{who.email ?? session.user.email}</strong>, which has no access to the operations
            console. Ask the owner to add this email as an operator, then reload.
          </p>
          <button type="button" className="btn" onClick={signOut}>
            Sign out
          </button>
        </div>
      </main>
    );
  }

  return (
    <OperatorProvider value={{ email: who.email, role: who.role }}>
      <ServicesProvider>
        <Shell email={who.email ?? session.user.email ?? ""} roleLabel={ROLE_LABEL[who.role]} onSignOut={signOut} />
      </ServicesProvider>
    </OperatorProvider>
  );
}

// ── Shell ──────────────────────────────────────────────────
// Order from OPS-FINAL D1. Money joins between Pricing and Change log in phase 2 (after PAY-01).
const NAV = [
  { to: "/today", label: "Today" },
  { to: "/dispatch", label: "Dispatch" },
  { to: "/orders", label: "Orders" },   // PROC-02
  { to: "/supply", label: "Supply" },   // SUPPLY (record 100): where Green Maestro delivers local materials
  { to: "/maestros", label: "Maestros" },
  { to: "/revisiones", label: "Reviews" },   // MDO-6 (English, Oct 14 2026; route unchanged)
  { to: "/territories", label: "Territories" },
  { to: "/property", label: "Property" },   // GM-TXT-03 D3: house stories
  { to: "/weather", label: "Weather" },   // WX-01
  { to: "/campaigns", label: "Campaigns" },
  { to: "/vecinos", label: "Neighbor flyers" },   // FLY-01
  { to: "/sugerencias", label: "Suggestions" },   // TELLGM-01
  { to: "/fotos", label: "Fotos" },   // CON-TOOLS-04
  { to: "/pages", label: "Pages" },   // PAGES-02
  { to: "/pricing", label: "Pricing" },
  { to: "/log", label: "Change log" },
];

const PRICING_TABS = [
  { to: "/pricing/services", label: "Services" },
  { to: "/pricing/quotes", label: "Quotes" },
  { to: "/pricing/seasons", label: "Seasonal multipliers" },
  { to: "/pricing/zips", label: "ZIP factors" },
  { to: "/pricing/settings", label: "Settings" },
];

function PricingLayout() {
  return (
    <>
      <nav className="subnav" aria-label="Pricing">
        {PRICING_TABS.map((t) => (
          <NavLink key={t.to} to={t.to} className={({ isActive }) => (isActive ? "subnav-link active" : "subnav-link")}>
            {t.label}
          </NavLink>
        ))}
      </nav>
      <Outlet />
    </>
  );
}

function Shell(props: { email: string; roleLabel: string; onSignOut: () => void }) {
  return (
    <div className="shell">
      <header className="topbar">
        <p className="brand-mark">Green Maestro Ops</p>
        <div className="who">
          <span className="who-email">{props.email}</span>
          <span className="who-role">{props.roleLabel}</span>
          <button type="button" className="btn quiet" onClick={props.onSignOut}>
            Sign out
          </button>
        </div>
      </header>
      <nav className="nav" aria-label="Sections">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}>
            {n.label}
          </NavLink>
        ))}
      </nav>
      <main className="content">
        <Routes>
          <Route path="/today" element={<Today />} />
          <Route path="/dispatch" element={<Dispatch />} />
          <Route path="/orders" element={<Orders />} />
          <Route path="/supply" element={<Supply />} />
          <Route path="/maestros" element={<Maestros />} />
          <Route path="/revisiones" element={<Revisiones />} />
          <Route path="/territories" element={<Territories />} />
          <Route path="/property" element={<Property />} />
          <Route path="/weather" element={<Weather />} />
          <Route path="/campaigns" element={<Campaigns />} />
          <Route path="/vecinos" element={<Vecinos />} />
          <Route path="/sugerencias" element={<Sugerencias />} />
          <Route path="/fotos" element={<Fotos />} />
          <Route path="/pages" element={<Pages />} />
          <Route path="/campaigns/new" element={<Suspense fallback={<p className="loading">Loading the campaign wizard…</p>}><CampaignWizard /></Suspense>} />
          <Route path="/pricing" element={<PricingLayout />}>
            <Route index element={<Navigate to="/pricing/services" replace />} />
            <Route path="services" element={<Services />} />
            <Route path="quotes" element={<Quotes />} />
            <Route path="seasons" element={<Seasons />} />
            <Route path="zips" element={<Zips />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="log" element={<Navigate to="/log" replace />} />
          </Route>
          <Route path="/log" element={<LogPage />} />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </main>
    </div>
  );
}
